// Analytics entièrement lazy — aucun code Amplitude dans le bundle initial.
// Chargé uniquement si l'utilisateur a donné son consentement.

import { posthogCapture } from './posthog';

const API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY || '';
const CONSENT_KEY = 'octoveto_analytics_consent';

// ── Consentement (synchrone, pas de dépendance externe) ──────────────────────

export function getAnalyticsConsent(): true | false | null {
  const v = localStorage.getItem(CONSENT_KEY);
  if (v === 'granted') return true;
  if (v === 'denied') return false;
  return null;
}

export function setAnalyticsConsent(granted: boolean) {
  localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  if (granted) initAmplitude();
}

// ── Init Amplitude (dynamic import → hors bundle initial) ────────────────────

export function initAmplitudeIfConsented() {
  if (getAnalyticsConsent() === true) initAmplitude();
}

export function initAmplitude() {
  if (!API_KEY) return;
  Promise.all([
    import('@amplitude/analytics-browser'),
    import('@amplitude/plugin-session-replay-browser'),
    import('@amplitude/engagement-browser'),
  ]).then(([amplitude, { plugin: sessionReplay }, { plugin: engagement }]) => {
    amplitude.add(sessionReplay({ sampleRate: 1 }));
    amplitude.add(engagement());
    amplitude.init(API_KEY, {
      serverUrl: 'https://api.eu.amplitude.com',
      autocapture: {
        pageViews: false,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: true,
      },
    });
    // Stocker la référence pour les appels suivants
    amplitudeRef = amplitude;
  }).catch(() => { /* Amplitude non critique */ });
}

// ── Référence runtime (null jusqu'à consentement + chargement) ───────────────

let amplitudeRef: typeof import('@amplitude/analytics-browser') | null = null;

// ── Tracking (no-op si pas encore chargé ou refus) ───────────────────────────

export function identifyUser(userId: string, properties?: Record<string, string | number | boolean>) {
  if (!amplitudeRef) return;
  amplitudeRef.setUserId(userId);
  if (properties) {
    const identify = new amplitudeRef.Identify();
    Object.entries(properties).forEach(([key, value]) => identify.set(key, value));
    amplitudeRef.identify(identify);
  }
}

export function resetUser() {
  amplitudeRef?.reset();
}

export function trackPageView(pageName: string, properties?: Record<string, unknown>) {
  if (!amplitudeRef || getAnalyticsConsent() !== true) return;
  amplitudeRef.track('page_view', { page: pageName, ...properties });
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (amplitudeRef && getAnalyticsConsent() === true) {
    amplitudeRef.track(eventName, properties);
  }
  // PostHog — prod only, no consent gate (no PII tracked)
  posthogCapture(eventName, properties);
}

// ── Events métier Octoveto ────────────────────────────────────────────────────

export const Analytics = {
  signupStarted: () => trackEvent('signup_started'),
  signupCompleted: (method: string) => trackEvent('signup_completed', { method }),
  loginCompleted: (method: string) => trackEvent('login_completed', { method }),
  logoutCompleted: () => trackEvent('logout_completed'),

  petAdded: (petType: string) => trackEvent('pet_added', { pet_type: petType }),
  petViewed: (petType: string) => trackEvent('pet_viewed', { pet_type: petType }),

  vaccineAdded: () => trackEvent('vaccine_added'),
  medicationAdded: () => trackEvent('medication_added'),
  weightLogged: () => trackEvent('weight_logged'),
  activityLogged: () => trackEvent('activity_logged'),

  subscriptionPageViewed: () => trackEvent('subscription_page_viewed'),
  subscriptionUpgradeClicked: () => trackEvent('subscription_upgrade_clicked'),
  subscriptionCompleted: (plan: string) => trackEvent('subscription_completed', { plan }),

  aiAssistantOpened: () => trackEvent('ai_assistant_opened'),
  gpsTrackingOpened: () => trackEvent('gps_tracking_opened'),
  socialForumOpened: () => trackEvent('social_forum_opened'),
  breederProfileViewed: () => trackEvent('breeder_profile_viewed'),

  pdfExported: () => trackEvent('pdf_exported'),
  qrCodeViewed: () => trackEvent('qr_code_viewed'),
};
